import { __assign, __read, __rest } from "tslib";
import React, { useState, useEffect, useRef, forwardRef, useMemo, useCallback, createElement, } from "react";
import { VariableSizeList, } from "react-window";
import { List } from "./List";
import { DropdownBox } from "../dropdown";
import { EmptyTip } from "../tips";
import { mergeRefs } from "../_util/merge-refs";
import { noop } from "../_util/noop";
import { useListScroll } from "../_util/use-list-scroll";
function Row(_a) {
    var data = _a.data, index = _a.index, style = _a.style;
    var _b = data[index], type = _b.type, text = _b.text, _c = _b.props, props = _c === void 0 ? {} : _c;
    if (type === "tips") {
        return (React.createElement(VStatusTip, __assign({}, props, { style: style }), text));
    }
    if (type === "group") {
        return (React.createElement(VGroupLabel, __assign({}, props, { style: style }), text));
    }
    if (type === "divider") {
        return React.createElement(VDivider, __assign({}, props, { style: style }));
    }
    return (React.createElement(VItem, __assign({}, props, { style: style, title: !props.tooltip && typeof text === "string" ? text : undefined }), text));
}
export var VirtualizedList = forwardRef(function VirtualizedList(_a, ref) {
    var _b = _a.virtual, virtual = _b === void 0 ? true : _b, props = __rest(_a, ["virtual"]);
    if (virtual) {
        return React.createElement(VList, __assign({}, props, { ref: ref }));
    }
    return React.createElement(NormalList, __assign({}, props, { ref: ref }));
});
VirtualizedList.displayName = "VirtualizedList";
var VList = forwardRef(function VList(_a, ref) {
    var _b, _c, _d;
    var _e = _a.items, items = _e === void 0 ? [] : _e, onScrollBottom = _a.onScrollBottom, virtualizedRef = _a.virtualizedRef, listHeight = _a.listHeight, 
    // ListProps
    type = _a.type, split = _a.split, className = _a.className, style = _a.style, container = _a.container, containerStyle = _a.containerStyle, containerClassName = _a.containerClassName, initialScrollOffsetIndex = _a.initialScrollOffsetIndex;
    var vRef = useRef(null);
    var listRef = useRef(null);
    var _f = __read(useState(undefined), 2), tipsSize = _f[0], setTipsSize = _f[1];
    var _g = __read(useState(undefined), 2), itemSize = _g[0], setItemSize = _g[1];
    var _h = __read(useState(undefined), 2), groupSize = _h[0], setGroupSize = _h[1];
    var _j = __read(useState(undefined), 2), dividerSize = _j[0], setDividerSize = _j[1];
    var _k = __read(useState(listHeight), 2), listSize = _k[0], setListSize = _k[1];
    var innerElementType = useMemo(function () {
        return forwardRef(function (_a, ref) {
            var _b = _a.style, innerStyle = _b === void 0 ? {} : _b, innerProps = __rest(_a, ["style"]);
            return (React.createElement(List, __assign({ ref: ref, type: type, split: split, className: className }, innerProps, { style: __assign(__assign(__assign({}, style), innerStyle), { maxHeight: "none", overflowY: "hidden" }) })));
        });
    }, [type, split, className, style]);
    var getItemSize = useCallback(function (index) {
        switch (items[index].type) {
            case "tips":
                return tipsSize || itemSize;
            case "group":
                return groupSize || itemSize;
            case "divider":
                return dividerSize || 9;
            default:
                return itemSize;
        }
    }, [dividerSize, groupSize, itemSize, items, tipsSize]);
    // VariableSizeList 默认按 index 缓存 style，需手动触发重置
    useEffect(function () {
        if (vRef.current) {
            items.forEach(function (_, i) { return vRef.current.resetAfterIndex(i, false); });
        }
    }, [groupSize, itemSize, items, tipsSize]);
    var itemsSize = useMemo(function () { return items.reduce(function (p, _, i) { return p + getItemSize(i); }, 0); }, [getItemSize, items]);
    function handleScroll(props) {
        if (itemsSize > listSize && listRef.current && onScrollBottom) {
            var _a = listRef.current, scrollHeight = _a.scrollHeight, scrollTop = _a.scrollTop, clientHeight = _a.clientHeight;
            if (scrollHeight <= Math.ceil(clientHeight + scrollTop + 1)) {
                onScrollBottom(props);
            }
        }
    }
    var height = Math.min(itemsSize, listSize);
    var initialScrollOffset = useMemo(function () {
        if (!initialScrollOffsetIndex || initialScrollOffsetIndex < 0) {
            return 0;
        }
        var offset = 0;
        for (var i = 0; i < initialScrollOffsetIndex; ++i) {
            offset += getItemSize(i);
        }
        // react-window 在 offset < height 时出现可能出现渲缺失
        return offset + getItemSize(initialScrollOffsetIndex) > height ? offset : 0;
    }, [getItemSize, height, initialScrollOffsetIndex]);
    var option = (_b = items.find(function (i) { return i.type === "option"; })) === null || _b === void 0 ? void 0 : _b.text;
    var tips = (_c = items.find(function (i) { return i.type === "tips"; })) === null || _c === void 0 ? void 0 : _c.text;
    var group = (_d = items.find(function (i) { return i.type === "group"; })) === null || _d === void 0 ? void 0 : _d.text;
    return (React.createElement(React.Fragment, null,
        [itemSize, listSize].every(function (s) { return typeof s !== "undefined"; }) && (React.createElement(VariableSizeList, { ref: mergeRefs(virtualizedRef, vRef), innerRef: ref, outerRef: listRef, height: height, width: "100%", itemCount: items.length, itemSize: getItemSize, innerElementType: innerElementType, onScroll: handleScroll, itemKey: function (index) { return items[index].key; }, itemData: items, initialScrollOffset: initialScrollOffset }, Row)),
        React.createElement(MirrorList, { container: container, containerStyle: containerStyle, containerClassName: containerClassName, onItemSizeChange: setItemSize, onListSizeChange: listHeight ? noop : setListSize, onTipsSizeChange: setTipsSize, onGroupSizeChange: setGroupSize, onDividerSizeChange: setDividerSize, tipsReference: tips, itemReference: option, groupReference: group, type: type, split: split, className: className, style: style })));
});
VList.displayName = "List";
function MirrorList(_a) {
    var itemReference = _a.itemReference, tipsReference = _a.tipsReference, groupReference = _a.groupReference, onTipsSizeChange = _a.onTipsSizeChange, onItemSizeChange = _a.onItemSizeChange, onGroupSizeChange = _a.onGroupSizeChange, onDividerSizeChange = _a.onDividerSizeChange, onListSizeChange = _a.onListSizeChange, _b = _a.container, container = _b === void 0 ? DropdownBox : _b, _c = _a.containerStyle, containerStyle = _c === void 0 ? {} : _c, containerClassName = _a.containerClassName, props = __rest(_a, ["itemReference", "tipsReference", "groupReference", "onTipsSizeChange", "onItemSizeChange", "onGroupSizeChange", "onDividerSizeChange", "onListSizeChange", "container", "containerStyle", "containerClassName"]);
    var tipsRef = useRef(null);
    var listRef = useRef(null);
    var groupRef = useRef(null);
    var itemRef = useRef(null);
    var dividerRef = useRef(null);
    useEffect(function () {
        if (itemRef.current) {
            onItemSizeChange(itemRef.current.clientHeight);
        }
        // tipsRef / groupRef 存在时才取高度
        if (tipsRef.current && tipsReference) {
            onTipsSizeChange(tipsRef.current.clientHeight);
        }
        if (groupRef.current && groupReference) {
            onGroupSizeChange(groupRef.current.clientHeight);
        }
        if (dividerRef.current) {
            onDividerSizeChange(dividerRef.current.clientHeight);
        }
        if (listRef.current) {
            onListSizeChange(listRef.current.clientHeight);
        }
    }, [
        tipsReference,
        itemReference,
        groupReference,
        onTipsSizeChange,
        onItemSizeChange,
        onGroupSizeChange,
        onListSizeChange,
        onDividerSizeChange,
    ]);
    return createElement(container, {
        style: __assign(__assign({}, containerStyle), { position: "absolute", top: -9999, left: 0 }),
        className: containerClassName,
    }, React.createElement(List, __assign({}, props, { ref: listRef }),
        React.createElement(List.StatusTip, { ref: tipsRef }, tipsReference || React.createElement(EmptyTip, null)),
        React.createElement(List.GroupLabel, { ref: groupRef }, groupReference || "MirrorGroup"),
        React.createElement(List.Item, { ref: itemRef }, itemReference || "MirrorItem"),
        React.createElement(List.Divider, { ref: dividerRef }),
        React.createElement("li", { style: { height: 3000 } }, "Holder")));
}
function VItem(_a) {
    var _b = _a.style, style = _b === void 0 ? {} : _b, props = __rest(_a, ["style"]);
    return (React.createElement(List.Item, __assign({}, props, { style: __assign(__assign({}, style), { boxSizing: "border-box" }) })));
}
function VGroupLabel(_a) {
    var _b = _a.style, style = _b === void 0 ? {} : _b, props = __rest(_a, ["style"]);
    return (React.createElement(List.GroupLabel, __assign({}, props, { style: __assign(__assign({}, style), { boxSizing: "border-box" }) })));
}
function VStatusTip(_a) {
    var _b = _a.style, style = _b === void 0 ? {} : _b, props = __rest(_a, ["style"]);
    return (React.createElement(List.StatusTip, __assign({}, props, { style: __assign(__assign({}, style), { boxSizing: "border-box" }) })));
}
function VDivider(_a) {
    var _b = _a.style, style = _b === void 0 ? {} : _b, props = __rest(_a, ["style"]);
    return (React.createElement(List.Divider, __assign({}, props, { style: __assign(__assign({}, style), { boxSizing: "border-box" }) })));
}
/**
 * 兼容虚拟滚动 List 接口的普通 List
 */
var NormalList = forwardRef(function NormalList(_a, ref) {
    var items = _a.items, type = _a.type, split = _a.split, onScrollBottom = _a.onScrollBottom, listHeight = _a.listHeight, _b = _a.style, style = _b === void 0 ? {} : _b, className = _a.className;
    var listRef = useRef(null);
    var scrollTo = useListScroll(listRef);
    useEffect(function () {
        var currentIndex = items.findIndex(function (item) { var _a; return (_a = item.props) === null || _a === void 0 ? void 0 : _a.current; });
        scrollTo(currentIndex);
    }, [items, scrollTo]);
    // 普通 List
    return (React.createElement(List, { ref: mergeRefs(ref, listRef), type: type, split: split, style: listHeight ? __assign({ maxHeight: listHeight }, style) : style, onScrollBottom: onScrollBottom, className: className }, items.map(function (item) {
        switch (item.type) {
            case "tips":
                return (React.createElement(List.StatusTip, __assign({}, (item.props || {}), { key: item.key }), item.text));
            case "group":
                return (React.createElement(List.GroupLabel, __assign({}, (item.props || {}), { key: item.key }), item.text));
            case "divider":
                return React.createElement(List.Divider, __assign({}, (item.props || {}), { key: item.key }));
        }
        return (React.createElement(List.Item, __assign({}, (item.props || {}), { key: item.key }), item.text));
    })));
});
NormalList.displayName = "List";
//# sourceMappingURL=VirtualizedList.js.map